if (!customElements.get('manual-search')) {
  customElements.define(
    'manual-search',
    class ManualSearch extends HTMLElement {
      async connectedCallback() {
        this.input = this.querySelector('.manual-search__input');
        this.clearButton = this.querySelector('.manual-search__clear');
        this.tabs = Array.from(this.querySelectorAll('[data-manual-tab]'));
        this.modeTabs = Array.from(this.querySelectorAll('[data-manual-mode]'));
        this.modePanels = Array.from(this.querySelectorAll('[data-manual-mode-panel]'));
        this.resultsContainer = this.querySelector('[data-manual-results]');
        this.emptyState = this.querySelector('[data-manual-empty]');
        this.countEl = this.querySelector('.manual-search__count');
        this.paginationEl = this.querySelector('[data-manual-pagination]');
        this.activeHeadingEl = this.querySelector('[data-manual-active-heading]');
        this.modeHeadingEl = this.querySelector('[data-manual-mode-heading]');

        const config = this.readConfig();

        await this.loadRemoteBatches(config);

        this.items = Array.from(this.querySelectorAll('[data-manual-item]'));

        if (!this.items.length) return;

        this.perPage = config.perPage > 0 ? config.perPage : 10;
        this.countText = config.countText || '';
        this.allText = config.allText || this.countText;
        this.showDefaultResults = config.showDefaultResults !== false;

        this.query = '';
        this.activeTag = null;
        this.page = 1;
        this.matched = this.items;

        const debouncedFilter = this.debounce(() => {
          this.page = 1;
          this.applyFilters();
        }, 200);

        if (this.input) {
          this.input.addEventListener('input', () => {
            this.query = this.input.value.trim().toLowerCase();
            this.clearButton?.toggleAttribute('hidden', this.query.length === 0);
            debouncedFilter();
          });
        }

        if (this.clearButton) {
          this.clearButton.addEventListener('click', () => {
            if (this.input) this.input.value = '';
            this.query = '';
            this.clearButton.setAttribute('hidden', '');
            this.page = 1;
            this.applyFilters();
            this.input?.focus();
          });
        }

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const tag = tab.getAttribute('data-tag');
            const wasActive = tab.getAttribute('aria-pressed') === 'true';

            this.tabs.forEach((t) => t.setAttribute('aria-pressed', 'false'));

            if (wasActive) {
              this.activeTag = null;
              this.activeHeadingEl?.setAttribute('hidden', '');
            } else {
              this.activeTag = tag;
              tab.setAttribute('aria-pressed', 'true');
              if (this.activeHeadingEl) {
                this.activeHeadingEl.textContent = tab.getAttribute('data-label') || '';
                this.activeHeadingEl.removeAttribute('hidden');
              }
            }

            this.page = 1;
            this.applyFilters();
          });
        });

        this.modeTabs.forEach((modeTab) => {
          modeTab.addEventListener('click', () => {
            if (modeTab.getAttribute('aria-selected') === 'true') return;

            const mode = modeTab.getAttribute('data-manual-mode');

            this.modeTabs.forEach((t) => t.setAttribute('aria-selected', t === modeTab ? 'true' : 'false'));
            this.modePanels.forEach((panel) => {
              panel.toggleAttribute('hidden', panel.getAttribute('data-manual-mode-panel') !== mode);
            });

            if (this.modeHeadingEl) {
              this.modeHeadingEl.textContent = modeTab.textContent.trim();
            }

            if (mode === 'search') {
              this.tabs.forEach((t) => t.setAttribute('aria-pressed', 'false'));
              this.activeTag = null;
              this.activeHeadingEl?.setAttribute('hidden', '');
            } else {
              if (this.input) this.input.value = '';
              this.query = '';
              this.clearButton?.setAttribute('hidden', '');
            }

            this.page = 1;
            this.applyFilters();
          });
        });

        if (this.paginationEl) {
          this.paginationEl.addEventListener('click', (event) => {
            const button = event.target.closest('[data-page]');
            if (!button || button.disabled) return;

            const targetPage = parseInt(button.getAttribute('data-page'), 10);
            if (!targetPage || targetPage === this.page) return;

            this.page = targetPage;
            this.renderPage();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }

        this.setAttribute('data-hydrated', '');
        this.applyFilters();
      }

      readConfig() {
        const configEl = this.querySelector('[data-manual-config]');
        if (!configEl) return {};

        try {
          return JSON.parse(configEl.textContent);
        } catch (error) {
          return {};
        }
      }

      async loadRemoteBatches(config) {
        const totalBatches = parseInt(config.totalBatches, 10) || 1;
        if (totalBatches <= 1 || !config.sectionId || !this.resultsContainer) return;

        const requests = [];
        for (let page = 2; page <= totalBatches; page++) {
          requests.push(this.fetchBatch(config.sectionId, page));
        }

        const batches = await Promise.all(requests);
        batches.forEach((rows) => {
          rows.forEach((row) => this.resultsContainer.appendChild(row));
        });
      }

      async fetchBatch(sectionId, page) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set('page', page);

          const response = await fetch(url.toString());
          if (!response.ok) return [];

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.from(doc.querySelectorAll('[data-manual-item]'));
        } catch (error) {
          return [];
        }
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn(...args), wait);
        };
      }

      applyFilters() {
        this.matched = this.items.filter((item) => {
          const title = item.getAttribute('data-title') || '';
          const tags = (item.getAttribute('data-tags') || '').split('|');
          const matchesQuery = this.query.length === 0 || title.includes(this.query);
          const matchesTag = !this.activeTag || tags.includes(this.activeTag);
          return matchesQuery && matchesTag;
        });

        this.totalPages = Math.max(1, Math.ceil(this.matched.length / this.perPage));
        if (this.page > this.totalPages) this.page = this.totalPages;

        this.renderPage();
      }

      renderPage() {
        this.items.forEach((item) => item.classList.remove('is-visible'));

        const isFiltered = this.query.length > 0 || !!this.activeTag;
        const idle = !this.showDefaultResults && !isFiltered;

        if (idle) {
          this.emptyState?.setAttribute('hidden', '');
          this.resultsContainer?.setAttribute('hidden', '');
          if (this.countEl) this.countEl.textContent = '';
          this.paginationEl?.setAttribute('hidden', '');
          this.paginationEl?.replaceChildren();
          return;
        }

        const start = (this.page - 1) * this.perPage;
        this.matched.slice(start, start + this.perPage).forEach((item) => item.classList.add('is-visible'));

        const hasResults = this.matched.length > 0;
        this.emptyState?.toggleAttribute('hidden', hasResults);
        this.resultsContainer?.toggleAttribute('hidden', !hasResults);

        if (this.countEl) {
          const template = isFiltered ? this.countText : this.allText;
          this.countEl.textContent = hasResults ? template.replace('{count}', this.matched.length) : '';
        }

        this.renderPagination();
      }

      renderPagination() {
        if (!this.paginationEl) return;

        if (this.totalPages <= 1) {
          this.paginationEl.setAttribute('hidden', '');
          this.paginationEl.replaceChildren();
          return;
        }

        this.paginationEl.removeAttribute('hidden');

        const fragment = document.createDocumentFragment();

        fragment.appendChild(this.buildPageButton(this.page - 1, '‹', this.page === 1, false, 'Trang trước'));

        this.getPageNumbers().forEach((item) => {
          if (item === '...') {
            fragment.appendChild(this.buildEllipsis());
          } else {
            fragment.appendChild(this.buildPageButton(item, String(item), false, item === this.page));
          }
        });

        fragment.appendChild(this.buildPageButton(this.page + 1, '›', this.page === this.totalPages, false, 'Trang sau'));

        this.paginationEl.replaceChildren(fragment);
      }

      getPageNumbers() {
        const total = this.totalPages;
        const current = this.page;
        const delta = 1;
        const pages = [1];

        const rangeStart = Math.max(2, current - delta);
        const rangeEnd = Math.min(total - 1, current + delta);

        if (rangeStart > 2) pages.push('...');

        for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

        if (rangeEnd < total - 1) pages.push('...');

        if (total > 1) pages.push(total);

        return pages;
      }

      buildEllipsis() {
        const span = document.createElement('span');
        span.className = 'manual-search__page-ellipsis';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '…';
        return span;
      }

      buildPageButton(page, label, disabled, isCurrent, ariaLabel) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'manual-search__page-button';
        if (isCurrent) button.classList.add('is-current');
        button.textContent = label;
        button.setAttribute('data-page', page);
        if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
        if (isCurrent) button.setAttribute('aria-current', 'page');
        if (disabled) button.disabled = true;
        return button;
      }
    }
  );
}
